/**
 * DiversionSystem.js
 *
 * Handles diversion logic when weather forces aircraft to land at alternate airports.
 * Finds suitable alternates, calculates costs, and manages the diversion flow.
 */

import { GLOBAL_AIRPORTS } from '../data/GlobalAirports.js';
import { AIRCRAFT_DATABASE } from '../data/aircraft.js';

// Diversion thresholds and limits
export const DIVERSION_CONFIG = {
  MIN_DISTANCE_TO_DEST_NM: 200,      // Don't divert if < 200nm from destination
  MAX_DIVERSION_DISTANCE_NM: 500,    // Won't divert to airports > 500nm away
  FUEL_RESERVE_PERCENT: 15,          // Must have 15% fuel reserve after diversion
  WEATHER_SEVERITY_TRIGGER: 'grounded', // Severity level that triggers diversion
  MIN_RUNWAY_LENGTH_FT: 5000,        // Minimum runway for diversion
};

// Cost factors for diversions
export const DIVERSION_COSTS = {
  BASE_HANDLING_COST: 5000,          // Base ground handling at diversion airport
  HOTEL_PER_PAX: 150,                // Hotel cost per passenger
  MEALS_PER_PAX: 50,                 // Meals cost per passenger
  REBOOKING_COST_PERCENT: 0.15,      // 15% of ticket revenue for rebooking
  FUEL_PREMIUM_PERCENT: 1.2,         // 20% premium on fuel at diversion airport
  CREW_OVERTIME_HOURLY: 200,         // Overtime pay per crew hour
};

/**
 * Check if a flight should divert based on destination weather
 *
 * @param {Object} flight - Active flight object
 * @param {Object} destWeather - Weather impact at destination
 * @param {number} distanceRemaining - NM remaining to destination
 * @returns {Object} { shouldDivert: boolean, reason: string }
 */
export function shouldDivert(flight, destWeather, distanceRemaining) {
  // Don't divert if too close to destination
  if (distanceRemaining < DIVERSION_CONFIG.MIN_DISTANCE_TO_DEST_NM) {
    return { shouldDivert: false, reason: 'Too close to destination' };
  }

  // Check weather severity
  if (!destWeather || destWeather.severity !== DIVERSION_CONFIG.WEATHER_SEVERITY_TRIGGER) {
    return { shouldDivert: false, reason: 'Weather acceptable' };
  }

  // Destination is grounded - diversion required
  return {
    shouldDivert: true,
    reason: destWeather.reason || 'Destination airport closed due to weather',
  };
}

/**
 * Calculate distance between two points using Haversine formula
 */
function calculateDistanceNM(lat1, lon1, lat2, lon2) {
  const R = 3440.065; // Earth radius in nautical miles
  const toRad = (deg) => deg * Math.PI / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}

/**
 * Find the best alternate airport for diversion
 *
 * @param {number} currentLat - Aircraft current latitude
 * @param {number} currentLon - Aircraft current longitude
 * @param {string} destination - Original destination ICAO (to exclude)
 * @param {Object} airportWeather - Weather data for all airports
 * @param {string} aircraftType - Aircraft type for runway requirements
 * @param {string|null} origin - Origin ICAO (to exclude, prevents origin===destination crash)
 * @returns {Object|null} Best alternate airport or null if none found
 */
export function findAlternateAirport(
  currentLat,
  currentLon,
  destination,
  airportWeather = {},
  aircraftType = 'A320',
  origin = null  // Optional: origin to exclude (prevents origin===destination crash)
) {
  const aircraft = AIRCRAFT_DATABASE[aircraftType] || AIRCRAFT_DATABASE['A320'];
  const minRunway = aircraft?.minRunway || DIVERSION_CONFIG.MIN_RUNWAY_LENGTH_FT;

  // Get all airports with their distances
  const candidates = [];

  for (const [icao, airport] of Object.entries(GLOBAL_AIRPORTS)) {
    // Skip original destination
    if (icao === destination) continue;

    // Skip origin airport - diverting back to origin would create origin===destination
    // which causes NaN in great-circle calculations and crashes Mapbox
    if (origin && icao === origin) continue;

    // Skip if airport is also grounded
    const weather = airportWeather[icao]?.impact;
    if (weather?.severity === 'grounded') continue;

    // Calculate distance from current position
    const distance = calculateDistanceNM(currentLat, currentLon, airport.lat, airport.lon);

    // Skip if too far
    if (distance > DIVERSION_CONFIG.MAX_DIVERSION_DISTANCE_NM) continue;

    // Prefer airports with good hub tier (major facilities)
    const tierScore = airport.hub_tier === 'major' ? 3 :
      airport.hub_tier === 'large' ? 2 :
        airport.hub_tier === 'medium' ? 1 : 0;

    // Weather penalty - prefer clear weather
    const weatherPenalty = weather?.severity === 'delay' ? 100 :
      weather?.severity === 'advisory' ? 50 : 0;

    // Combined score (lower is better)
    const score = distance + weatherPenalty - (tierScore * 50);

    candidates.push({
      icao,
      name: airport.name,
      city: airport.city,
      country: airport.country,
      lat: airport.lat,
      lon: airport.lon,
      distance,
      hubTier: airport.hub_tier,
      weatherSeverity: weather?.severity || 'clear',
      score,
    });
  }

  // Sort by score (lower is better)
  candidates.sort((a, b) => a.score - b.score);

  // Return best candidate or null
  return candidates.length > 0 ? candidates[0] : null;
}

/**
 * Calculate all costs associated with a diversion
 *
 * @param {Object} flight - The diverted flight
 * @param {Object} alternateAirport - The diversion destination
 * @param {number} extraFuelLbs - Additional fuel burned
 * @returns {Object} Cost breakdown
 */
export function calculateDiversionCosts(flight, alternateAirport, extraFuelLbs = 0) {
  const passengers = flight.passengers ||
    flight.preCalculatedEarnings?.passengers || 100;
  const ticketRevenue = flight.preCalculatedEarnings?.revenue ||
    flight.revenue || (passengers * 250);

  // Fuel cost at premium rate
  const fuelPricePerLb = 0.35 * DIVERSION_COSTS.FUEL_PREMIUM_PERCENT;
  const fuelCost = Math.round(extraFuelLbs * fuelPricePerLb);

  // Passenger accommodation
  const hotelCost = Math.round(passengers * DIVERSION_COSTS.HOTEL_PER_PAX);
  const mealsCost = Math.round(passengers * DIVERSION_COSTS.MEALS_PER_PAX);

  // Rebooking costs
  const rebookingCost = Math.round(ticketRevenue * DIVERSION_COSTS.REBOOKING_COST_PERCENT);

  // Ground handling
  const handlingCost = DIVERSION_COSTS.BASE_HANDLING_COST;

  // Crew overtime (assume 4 extra hours)
  const crewOvertimeCost = 4 * DIVERSION_COSTS.CREW_OVERTIME_HOURLY;

  const totalCost = fuelCost + hotelCost + mealsCost + rebookingCost + handlingCost + crewOvertimeCost;

  return {
    fuelCost,
    hotelCost,
    mealsCost,
    rebookingCost,
    handlingCost,
    crewOvertimeCost,
    totalCost,
    breakdown: [
      { name: 'Ground Handling', amount: handlingCost },
      { name: 'Passenger Hotels', amount: hotelCost },
      { name: 'Passenger Meals', amount: mealsCost },
      { name: 'Rebooking Fees', amount: rebookingCost },
      { name: 'Extra Fuel', amount: fuelCost },
      { name: 'Crew Overtime', amount: crewOvertimeCost },
    ],
  };
}

/**
 * Create a diversion record for a flight
 *
 * @param {Object} flight - Original flight
 * @param {Object} alternateAirport - Diversion destination
 * @param {string} reason - Reason for diversion
 * @param {Object} costs - Cost breakdown from calculateDiversionCosts
 * @returns {Object} Diversion record to store on flight
 */
export function createDiversionRecord(flight, alternateAirport, reason, costs) {
  return {
    originalDestination: flight.destination,
    divertedTo: alternateAirport.icao,
    divertedToName: alternateAirport.name || alternateAirport.city,
    divertedAt: new Date().toISOString(),
    reason,
    distanceToAlternate: alternateAirport.distance,
    costs,
    status: 'diverted', // Will be updated to 'completed_diverted' when landed
  };
}

/**
 * Process a diversion - updates flight state with diversion info
 *
 * @param {Object} flight - Flight to divert
 * @param {Object} alternateAirport - Where to divert
 * @param {string} reason - Why diverting
 * @returns {Object} Updated flight with diversion data
 */
export function initiateDiversion(flight, alternateAirport, reason) {
  // Calculate extra distance/fuel
  const aircraft = AIRCRAFT_DATABASE[flight.aircraftType] || AIRCRAFT_DATABASE['A320'];
  const fuelBurnRate = aircraft?.fuelBurn || 2500; // lbs/hr
  const cruiseSpeed = aircraft?.cruiseSpeed || 450; // knots

  // Extra fuel for diversion (rough estimate)
  const diversionHours = alternateAirport.distance / cruiseSpeed;
  const extraFuelLbs = Math.round(fuelBurnRate * diversionHours);

  const costs = calculateDiversionCosts(flight, alternateAirport, extraFuelLbs);
  const diversionRecord = createDiversionRecord(flight, alternateAirport, reason, costs);

  return {
    ...flight,
    diverted: true,
    divertedTo: alternateAirport.icao,
    diversionRecord,
    // Update destination to alternate
    originalDestination: flight.destination,
    destination: alternateAirport.icao,
    // Reset flight progress for new destination (simplified - in reality would need recalculation)
    status: 'diverted',
  };
}

/**
 * Check if a flight can safely divert (has enough fuel)
 *
 * @param {Object} flight - Flight to check
 * @param {Object} alternateAirport - Potential diversion destination
 * @returns {Object} { canDivert: boolean, fuelRemaining: number, reason: string }
 */
export function canSafelyDivert(flight, alternateAirport) {
  const aircraft = AIRCRAFT_DATABASE[flight.aircraftType] || AIRCRAFT_DATABASE['A320'];
  const fuelBurnRate = aircraft?.fuelBurn || 2500;
  const cruiseSpeed = aircraft?.cruiseSpeed || 450;
  const fuelCapacity = aircraft?.fuelCapacity || 40000;

  // Estimate current fuel (based on flight progress)
  const progress = flight.distanceFraction || flight.overallProgress || 0.5;
  const totalFuelBurned = progress * fuelCapacity * 0.8; // Assume started with 80% fuel
  const currentFuel = (fuelCapacity * 0.8) - totalFuelBurned;

  // Fuel needed for diversion
  const diversionHours = alternateAirport.distance / cruiseSpeed;
  const fuelNeeded = fuelBurnRate * diversionHours;
  const fuelWithReserve = fuelNeeded * (1 + DIVERSION_CONFIG.FUEL_RESERVE_PERCENT / 100);

  const canDivert = currentFuel >= fuelWithReserve;
  const fuelRemaining = currentFuel - fuelNeeded;

  return {
    canDivert,
    currentFuel: Math.round(currentFuel),
    fuelNeeded: Math.round(fuelNeeded),
    fuelRemaining: Math.round(fuelRemaining),
    reason: canDivert ? 'Fuel sufficient' : 'Insufficient fuel for diversion',
  };
}

export default {
  DIVERSION_CONFIG,
  DIVERSION_COSTS,
  shouldDivert,
  findAlternateAirport,
  calculateDiversionCosts,
  createDiversionRecord,
  initiateDiversion,
  canSafelyDivert,
};
